export interface TaxableValueMatchRow {
    assessedValue?: number;
    marketValue?: number;
    taxAmount?: number;
    sequence?: number;
}

export interface SavedTaxableValue extends TaxableValueMatchRow {
    annualAssessmentTaxableValueId: number;
}

// Works out which saved taxable value each row is, so that the server updates the saved row in place instead of
// deleting it and adding a new one. The rows may have been recalculated, sorted or renumbered on the screen, so their
// position says nothing about which saved row they are. Each saved row is claimed at most once, by the closest match
// first, and the saved order is only used to pair up whatever no value could tell apart. The ids are returned in the
// order of the rows, with undefined for a row that matched no saved row.
export function matchTaxableValueIds(rows: TaxableValueMatchRow[], savedRows: SavedTaxableValue[]): number[] {
    const availableSavedRows = (savedRows ?? [])
        .filter((savedRow) => savedRow.annualAssessmentTaxableValueId > 0)
        .sort((x, y) => (x.sequence ?? 0) - (y.sequence ?? 0));
    const matchedIds: number[] = rows.map(() => undefined);

    const claimMatchingIds = (isMatch: (row: TaxableValueMatchRow, savedRow: SavedTaxableValue) => boolean): void => {
        rows.forEach((row, index) => {
            if (matchedIds[index] !== undefined) {
                return;
            }

            const matchIndex = availableSavedRows.findIndex((savedRow) => isMatch(row, savedRow));

            if (matchIndex >= 0) {
                matchedIds[index] = availableSavedRows[matchIndex].annualAssessmentTaxableValueId;
                availableSavedRows.splice(matchIndex, 1);
            }
        });
    };

    claimMatchingIds(
        (row, savedRow) =>
            isSameValue(row, savedRow) && roundTaxAmount(row.taxAmount) === roundTaxAmount(savedRow.taxAmount),
    );
    claimMatchingIds(isSameValue);
    claimMatchingIds((row, savedRow) => row.sequence !== undefined && row.sequence === savedRow.sequence);
    claimMatchingIds(() => true);

    return matchedIds;
}

function isSameValue(row: TaxableValueMatchRow, savedRow: TaxableValueMatchRow): boolean {
    return row.assessedValue === savedRow.assessedValue && row.marketValue === savedRow.marketValue;
}

function roundTaxAmount(taxAmount: number): number {
    return parseFloat((taxAmount ?? 0).toFixed(2));
}
